import React, { forwardRef } from 'react'

export enum WooNotesButtonSize {
  Tiny = 'tiny',
  Slim = 'slim',
  Normal = 'normal',
  Large = 'large',
}

type SizeStyle = {
  padding: string
  fontSize: number
  borderRadius: number
  borderWidth: number
  shadowReserve: number
}

const SIZE_MAP: Record<WooNotesButtonSize, SizeStyle> = {
  tiny: { padding: '3px 7px', fontSize: 12, borderRadius: 10, borderWidth: 2, shadowReserve: 10 },
  slim: { padding: '6px 10px', fontSize: 13, borderRadius: 10, borderWidth: 2, shadowReserve: 10 },
  normal: { padding: '12px 18px', fontSize: 18, borderRadius: 16, borderWidth: 3, shadowReserve: 14 },
  large: { padding: '16px 26px', fontSize: 22, borderRadius: 20, borderWidth: 4, shadowReserve: 18 },
}

export enum WooNotesButtonColor {
  Green = 'green',
  Red = 'red',
  Blue = 'blue',
  Purple = 'purple',
  Yellow = 'yellow',
  White = 'white',
  Black = 'black',
}

type ColorStyle = {
  border: string
  shadow: string
  text: string
  bg: string
  bgDisabled: string
}

const COLOR_MAP: Record<WooNotesButtonColor, ColorStyle> = {
  green: {
    border: '#0E4A2C',
    shadow: '#0E4A2C',
    text: '#FFFFFF',
    bg: 'linear-gradient(180deg, #abdf63 0%, #67D34A 55%, #35B63A 100%)',
    bgDisabled: 'linear-gradient(180deg, #B8E8C9 0%, #8FD7AD 100%)',
  },
  red: {
    border: '#6A0F0F',
    shadow: '#6A0F0F',
    text: '#FFFFFF',
    bg: 'linear-gradient(180deg, #FF9A9A 0%, #FF4F4F 60%, #D63030 100%)',
    bgDisabled: 'linear-gradient(180deg, #FFD1D1 0%, #FF9C9C 100%)',
  },
  blue: {
    border: '#0B3A6F',
    shadow: '#0B3A6F',
    text: '#FFFFFF',
    bg: 'linear-gradient(180deg, #9FD0FF 0%, #4FA3FF 60%, #2E7DE0 100%)',
    bgDisabled: 'linear-gradient(180deg, #CFE6FF 0%, #9FCBFF 100%)',
  },
  purple: {
    border: '#4A1B6A',
    shadow: '#4A1B6A',
    text: '#FFFFFF',
    bg: 'linear-gradient(180deg, #E2B8FF 0%, #B56BFF 60%, #8B3ED6 100%)',
    bgDisabled: 'linear-gradient(180deg, #F0D9FF 0%, #D6AFFF 100%)',
  },
  yellow: {
    border: '#8A6A00',
    shadow: '#8A6A00',
    text: '#3A2E00',
    bg: 'linear-gradient(180deg, #FFF3A0 0%, #FFD83D 60%, #F0B800 100%)',
    bgDisabled: 'linear-gradient(180deg, #FFF8CC 0%, #FFE88A 100%)',
  },
  white: {
    border: '#CFCFCF',
    shadow: '#B5B5B5',
    text: '#222222',
    bg: 'linear-gradient(180deg, #FFFFFF 0%, #F1F1F1 100%)',
    bgDisabled: 'linear-gradient(180deg, #FFFFFF 0%, #F6F6F6 100%)',
  },
  black: {
    border: '#111111',
    shadow: '#111111',
    text: '#FFFFFF',
    bg: 'linear-gradient(180deg, #555555 0%, #2B2B2B 100%)',
    bgDisabled: 'linear-gradient(180deg, #777777 0%, #444444 100%)',
  },
}

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: any
  fullWidth?: boolean
  color?: WooNotesButtonColor
  size?: WooNotesButtonSize

  /** Hvis false: ingen 3D/skygge/translateY-press, og ingen ekstra marginBottom reserve. Default: true */
  raised?: boolean

  /** ✅ Hvis true: knappen ser ud som om den er trykket ned (valgt) */
  pressed?: boolean
}

export const WooNotesButton = forwardRef<HTMLButtonElement, Props>(
  ({ children, fullWidth, style, color = WooNotesButtonColor.Green, size = WooNotesButtonSize.Normal, raised = true, pressed = false, ...props }, ref) => {
    const theme = COLOR_MAP[color]
    const sizing = SIZE_MAP[size]

    const shadowRest = raised
      ? props.disabled
        ? `0 6px 0 ${theme.shadow}, 0 10px 18px rgba(0,0,0,0.15)`
        : `0 7px 0 ${theme.shadow}, 0 12px 22px rgba(0,0,0,0.22)`
      : 'none'

    const shadowPressed = raised ? `0 4px 0 ${theme.shadow}, 0 10px 18px rgba(0,0,0,0.18)` : 'none'

    const baseTransform = raised && !props.disabled && pressed ? 'translateY(3px)' : 'translateY(0px)'
    const baseShadow = raised && !props.disabled && pressed ? shadowPressed : shadowRest
    const baseFilter = pressed ? 'brightness(0.995)' : 'none'

    return (
      <button
        ref={ref}
        {...props}
        aria-pressed={pressed}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,

          padding: sizing.padding,
          borderRadius: sizing.borderRadius,
          border: `${sizing.borderWidth}px solid ${theme.border}`,

          marginBottom: raised ? sizing.shadowReserve : 0,

          cursor: props.disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          width: fullWidth ? '100%' : undefined,

          color: theme.text,
          background: props.disabled ? theme.bgDisabled : theme.bg,

          boxShadow: baseShadow,
          transform: baseTransform,
          filter: baseFilter,

          fontWeight: 900,
          fontSize: sizing.fontSize,
          letterSpacing: 0.2,

          transition: 'transform 120ms ease, box-shadow 120ms ease, filter 120ms ease',

          ...style,
        }}
        onMouseDown={e => {
          props.onMouseDown?.(e)
          if (props.disabled) return
          if (!raised) return
          if (pressed) return // ✅ låst som nedtrykket allerede
          e.currentTarget.style.transform = 'translateY(3px)'
          e.currentTarget.style.boxShadow = shadowPressed
        }}
        onMouseUp={e => {
          props.onMouseUp?.(e)
          if (props.disabled) return
          if (!raised) return
          if (pressed) return
          e.currentTarget.style.transform = 'translateY(0px)'
          e.currentTarget.style.boxShadow = shadowRest
        }}
        onMouseLeave={e => {
          props.onMouseLeave?.(e)
          if (props.disabled) return
          if (!raised) return

          // ✅ når man forlader, gå tilbage til "base state"
          e.currentTarget.style.transform = baseTransform
          e.currentTarget.style.boxShadow = baseShadow
          e.currentTarget.style.filter = baseFilter
        }}
        onMouseEnter={e => {
          props.onMouseEnter?.(e)
          if (props.disabled) return
          // ✅ lille hover — men ikke “løfter” en pressed knap
          e.currentTarget.style.filter = pressed ? 'brightness(1.0)' : 'brightness(1.02)'
        }}
      >
        {children}
      </button>
    )
  },
)

WooNotesButton.displayName = 'WooNotesButton'
